// 🔑 HOW STAFF SEE A BORROWER'S IDENTITY — FROM THE IDENTITY RECORD, NEVER FROM DOCUMENTS ON FILE (Jesse, 2026-10-20/22).
// "Manual ID photos uploaded at step 4 but never submitted show 'Government-Issued Photo ID — completed'" — an upload is
// not a submission, and a submission is not a verification. One helper for every staff identity label and row.
//
// States: not started / photos uploaded, not submitted / checking / needs fixing / flagged / on hold / verified
// (Persona or manual, with date and method). A Persona-verified borrower with abandoned manual photos is
// "Verified — Persona (date)", the photos listed as "uploaded, not submitted", never as completing anything.
// 2026-10-19 (item 13): a flagged or held ID reads as flagged / on hold, never "verified", whatever
// users.identity_verified says.

const dayOf = (d) => {
  if (!d) return null;
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? null : t.toISOString().slice(0, 10);
};

/**
 * @param {object} user  users row (identity_verified)
 * @param {object} iv    identity_verifications row (or null)
 * @param {Array}  documents  the borrower's documents: step-4 photos live there (source 'manual_id') until submitted
 * @returns {{ state, label, method, date, manualPhotos }}
 *   state: 'not_started' | 'photos_uploaded' | 'checking' | 'needs_fix' | 'flagged' | 'on_hold' | 'verified'
 *   method: 'persona' | 'manual_automatic' | 'manual_staff' | null
 *   manualPhotos: 'none' | 'uploaded_not_submitted' | 'submitted'
 */
export function identityStaffState(user, iv, documents = []) {
  const st = String((iv && iv.status) || '').toLowerCase();
  const slotPhotos = (Array.isArray(documents) ? documents : []).some((d) => d && String(d.source || '') === 'manual_id' && !d.replaced_by_document_id);
  const hasPhotos = slotPhotos || !!(iv && (iv.manual_front_url || iv.manual_back_url || iv.front_document_id || iv.back_document_id));
  const submitted = !!(iv && iv.manual_submitted_at);
  const manualPhotos = !hasPhotos ? 'none' : submitted ? 'submitted' : 'uploaded_not_submitted';
  const out = (state, label, method = null, date = null) => ({ state, label, method, date, manualPhotos });

  if (st === 'id_review_hold') return out('on_hold', '⏸ On hold — under review');
  if (st === 'flagged') return out('flagged', '🚩 Flagged — under review');
  if (user && Number(user.identity_verified) === 1) {
    const path = String((iv && iv.verification_path) || '').toLowerCase();
    const staff = !!(iv && (iv.admin_verified_at || Number(iv.verified_manually) === 1)) || st === 'accepted';
    const userMethod = String((user && user.identity_verified_method) || '').toLowerCase();
    const method = path === 'manual_ai' ? 'manual_automatic'
      : (path === 'persona' || userMethod === 'persona') ? 'persona'
        : staff ? 'manual_staff' : 'persona';
    const date = dayOf(method === 'manual_staff' ? (iv && iv.admin_verified_at) : method === 'manual_automatic' ? (iv && (iv.ai_checked_at || iv.updated_at)) : (iv && iv.updated_at));
    const how = method === 'persona' ? 'Persona' : method === 'manual_automatic' ? 'uploaded ID, automatic check' : 'uploaded ID, staff review';
    return out('verified', `✅ Verified — ${how}${date ? ` (${date})` : ''}`, method, date);
  }
  if (st === 'checking' || st === 'manual_review' || (submitted && !['needs_fix', 'declined', 'expired'].includes(st))) {
    return out('checking', st === 'manual_review' ? '⏳ Checking — with our team' : '⏳ Checking');
  }
  if (st === 'needs_fix' || st === 'declined' || st === 'expired' || st === 'rejected') return out('needs_fix', '⚠️ Needs fixing — borrower re-uploading');
  if (hasPhotos || st === 'not_submitted') return out('photos_uploaded', '📎 Photos uploaded, not submitted');
  return out('not_started', '❌ Not started');
}

/** The one-line staff label (kept for every existing caller). */
export function identityAdminLabel(user, iv, documents = []) {
  return identityStaffState(user, iv, documents).label;
}
